// ###### IMPORTS
import React, { useState, useEffect } from 'react';
import { XMarkIcon, PencilSquareIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { updateBalanceSnapshotItem } from '../../viewServices/assets_api';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';

// ###### HELPERS
const parseDecimal = (v) => {
  if (v === '' || v == null) return null;
  const n = parseFloat(String(v).replace(',', '.'));
  return Number.isNaN(n) ? null : n;
};

const formatDisplayValue = (v, decimals = 2) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

// ###### COMPONENT
const ModalSnapshotItemEdit = ({ selectedItem, onClose, onSuccess }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [edited, setEdited] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (selectedItem) {
      setEdited({
        snapshot_value: selectedItem.snapshot_value != null ? String(selectedItem.snapshot_value) : '',
        snapshot_quantity: selectedItem.snapshot_quantity != null ? String(selectedItem.snapshot_quantity) : '',
        market_unitary_value: selectedItem.market_unitary_value != null ? String(selectedItem.market_unitary_value) : '',
      });
      setIsEditing(false);
    }
  }, [selectedItem]);

  if (!selectedItem || !edited) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEdited((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const snapshotValue = parseDecimal(edited.snapshot_value);
    if (snapshotValue === null) {
      toast.error('Valor do snapshot é obrigatório e deve ser um número válido.');
      return;
    }
    const snapshotQuantity = parseDecimal(edited.snapshot_quantity);
    if (edited.snapshot_quantity?.trim() !== '' && snapshotQuantity === null) {
      toast.error('Quantidade inválida.');
      return;
    }
    const marketUnitaryValue = parseDecimal(edited.market_unitary_value);
    if (edited.market_unitary_value?.trim() !== '' && marketUnitaryValue === null) {
      toast.error('Valor unitário de mercado inválido.');
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        snapshot_value: snapshotValue,
        snapshot_quantity: snapshotQuantity ?? null,
        market_unitary_value: marketUnitaryValue ?? null,
      };
      const res = await updateBalanceSnapshotItem(selectedItem.id, payload);
      toast.success('Item atualizado.');
      onSuccess?.(res.data);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Erro ao atualizar item.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-4 md:p-6 rounded-2xl shadow w-80 md:w-96 relative max-h-[90vh] overflow-y-auto">
        {submitting && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/90 rounded-2xl">
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
              <SpinnerLoad />
            </div>
          </div>
        )}

        <div className="absolute top-2 right-2 flex gap-2">
          {!isEditing && (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              disabled={submitting}
              className={`text-gray-500 ${submitting ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-700'}`}
              title="Editar"
            >
              <PencilSquareIcon className="h-5 w-5" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className={`text-gray-500 ${submitting ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-700'}`}
            title="Fechar"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <h2 className="text-lg font-semibold mb-4 pr-10">
          {isEditing ? 'Editar item do snapshot' : 'Detalhes do item'}
        </h2>

        <form className="space-y-4" onSubmit={handleUpdate}>
          {/* Read-only: asset info */}
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Asset</label>
            <div className="px-2 py-1.5 border rounded-md text-sm bg-gray-50 text-gray-600 flex items-center gap-2">
              {selectedItem.asset_category_code && (
                <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1 py-0.5 rounded shrink-0">
                  {selectedItem.asset_category_code}
                </span>
              )}
              <span className="text-xs font-mono text-gray-500 bg-gray-50 border border-gray-200 px-1 py-0.5 rounded shrink-0">
                {selectedItem.asset_code || '—'}
              </span>
              <span className="font-medium text-gray-800 truncate">
                {selectedItem.asset_name || '—'}
              </span>
            </div>
          </div>

          {/* snapshot_value */}
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">
              Valor do snapshot (R$) <span className="text-red-400">*</span>
            </label>
            {isEditing ? (
              <input
                type="text"
                name="snapshot_value"
                value={edited.snapshot_value}
                onChange={handleInputChange}
                placeholder="0,00"
                className="w-full px-2 py-1.5 border rounded-md text-sm tabular-nums"
                disabled={submitting}
                inputMode="decimal"
                required
              />
            ) : (
              <div className="px-2 py-1.5 border rounded-md text-sm bg-gray-50 text-gray-700 tabular-nums">
                R$ {formatDisplayValue(selectedItem.snapshot_value)}
              </div>
            )}
          </div>

          {/* snapshot_quantity */}
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Quantidade (opcional)</label>
            {isEditing ? (
              <input
                type="text"
                name="snapshot_quantity"
                value={edited.snapshot_quantity}
                onChange={handleInputChange}
                placeholder="Ex: 100"
                className="w-full px-2 py-1.5 border rounded-md text-sm tabular-nums"
                disabled={submitting}
                inputMode="decimal"
              />
            ) : (
              <div className="px-2 py-1.5 border rounded-md text-sm bg-gray-50 text-gray-700 tabular-nums">
                {formatDisplayValue(selectedItem.snapshot_quantity, 8)}
              </div>
            )}
          </div>

          {/* market_unitary_value */}
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Valor unitário de mercado (opcional)</label>
            {isEditing ? (
              <input
                type="text"
                name="market_unitary_value"
                value={edited.market_unitary_value}
                onChange={handleInputChange}
                placeholder="0,00000000"
                className="w-full px-2 py-1.5 border rounded-md text-sm tabular-nums"
                disabled={submitting}
                inputMode="decimal"
              />
            ) : (
              <div className="px-2 py-1.5 border rounded-md text-sm bg-gray-50 text-gray-700 tabular-nums">
                {formatDisplayValue(selectedItem.market_unitary_value, 8)}
              </div>
            )}
          </div>

          {isEditing && (
            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className={`flex-1 h-10 text-white rounded-2xl ${submitting ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
              >
                {submitting ? 'Salvando...' : 'Salvar'}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 h-10 rounded-2xl border border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                Cancelar
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ModalSnapshotItemEdit;
